import { scheduleDateAtom } from '@/atoms/sessionAtoms';
import { useIsScheduleSidebarDragDropKilled } from '@/features/flags';
import {
  isDraggingUpcomingSessionsTabAtom,
  isUpcomingSessionsListLoadingAtom,
} from '@/features/upcomingSessionTab/atoms';
import { useUpcomingSessionsList } from '@/features/upcomingSessionTab/hooks/useUpcomingSessionsList';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo, useRef } from 'react';

export function useSidebarUploadArea() {
  const scheduleDate = useAtomValue(scheduleDateAtom);
  const { upcomingSessions } = useUpcomingSessionsList();
  const isUpcomingSessionsListLoading = useAtomValue(isUpcomingSessionsListLoadingAtom);
  const { integrationEhrProvider } = useIntegrationEhrInfo();
  const isDragging = useAtomValue(isDraggingUpcomingSessionsTabAtom);
  const setIsDragging = useSetAtom(isDraggingUpcomingSessionsTabAtom);
  const dragCounterRef = useRef(0);
  const isScheduleSidebarDragDropKilled = useIsScheduleSidebarDragDropKilled();

  const stopPropagationWhenKilled = useCallback(
    (e: React.DragEvent) => {
      if (isScheduleSidebarDragDropKilled) {
        e.stopPropagation();
      }
    },
    [isScheduleSidebarDragDropKilled]
  );

  // Keep propagation enabled by default: react-dropzone resolves dragenter/drop
  // asynchronously and discards them if an ancestor stops the shared event.
  // The kill switch deliberately restores that old behaviour for rapid mitigation.
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      stopPropagationWhenKilled(e);
      dragCounterRef.current++;
      if (dragCounterRef.current === 1) {
        setIsDragging(true);
      }
    },
    [setIsDragging, stopPropagationWhenKilled]
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      stopPropagationWhenKilled(e);
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    },
    [setIsDragging, stopPropagationWhenKilled]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      stopPropagationWhenKilled(e);
      dragCounterRef.current = 0;
      setIsDragging(false);
    },
    [setIsDragging, stopPropagationWhenKilled]
  );

  const showSidebarUploadArea = useMemo(() => {
    return (
      (!scheduleDate &&
        !upcomingSessions.length &&
        !isUpcomingSessionsListLoading &&
        !integrationEhrProvider) ||
      isDragging
    );
  }, [
    scheduleDate,
    upcomingSessions.length,
    isUpcomingSessionsListLoading,
    integrationEhrProvider,
    isDragging,
  ]);

  return {
    showSidebarUploadArea,
    handleDragEnter,

    handleDragLeave,
    handleDrop,
  };
}
